import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../components/Avatar';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { isNative } from '../lib/platform';
import { useConfirm } from '../components/ConfirmModal';
import { LargeTitle, Section, Card, ListGroup, tap } from '../components/ui';
import { CaretRight, SignOut, Trash, User, EnvelopeSimple, LockSimple } from '@phosphor-icons/react';

// One tappable settings row
function SettingsRow({ icon: Icon, label, value, onClick, danger = false }) {
  return (
    <button
      type="button"
      onClick={() => { tap(); onClick(); }}
      className="w-full flex items-center gap-3.5 px-4 min-h-[56px] py-3 text-left active:bg-[#F6F4FA]"
    >
      <span className={`w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 ${danger ? 'bg-[#FDECEC] text-[#D92D20]' : 'bg-primary-10 text-primary'}`}>
        <Icon size={18} weight="bold" />
      </span>
      <span className={`flex-1 text-[16px] font-medium ${danger ? 'text-[#D92D20]' : 'text-[#111]'}`}>{label}</span>
      {value && <span className="text-[15px] text-gray-500 truncate max-w-[45%]">{value}</span>}
      {!danger && <CaretRight size={16} weight="bold" className="text-gray-300 flex-shrink-0" />}
    </button>
  );
}

function EditModal({ title, fields, onClose, onSave }) {
  const [values, setValues] = useState(
    Object.fromEntries(fields.map(f => [f.key, f.defaultValue || '']))
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-[28px] md:rounded-xl p-6 pb-[calc(24px+env(safe-area-inset-bottom))] md:pb-6 w-full md:max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">{title}</h3>
        <form onSubmit={handleSubmit}>
          {fields.map((field) => (
            <div key={field.key} className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">{field.label}</label>
              <input
                type={field.type || 'text'}
                value={values[field.key]}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                required
                placeholder={field.placeholder || ''}
                className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2.5 md:py-2 text-[16px] md:text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
              />
            </div>
          ))}
          {error && (
            <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex gap-3 [&>button]:flex-1 [&>button]:h-12 md:[&>button]:flex-none md:[&>button]:h-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50">
              {loading ? 'Saving...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteModal({ clinicId, userId, onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.settings.invite(clinicId, {
        email,
        first_name: firstName,
        last_name: lastName,
        created_by: userId,
      });
      onInvited();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-[28px] md:rounded-xl p-6 pb-[calc(24px+env(safe-area-inset-bottom))] md:pb-6 w-full md:max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">Invite Member</h3>
        <form onSubmit={handleSubmit}>
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-text mb-1">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2.5 md:py-2 text-[16px] md:text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-text mb-1">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2.5 md:py-2 text-[16px] md:text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2.5 md:py-2 text-[16px] md:text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          {error && (
            <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 [&>button]:flex-1 [&>button]:h-12 md:[&>button]:flex-none md:[&>button]:h-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50">
              {loading ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClinicSection({ clinic, userId, subscription, onRefresh }) {
  const navigate = useNavigate();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [clinicNameModalOpen, setClinicNameModalOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { confirm, confirmModal } = useConfirm();

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const session = await api.billing.createPortalSession(clinic.id);
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (err) {
      console.error('Portal session failed:', err);
    }
    setPortalLoading(false);
  };

  const handleRemoveMember = async (member) => {
    const isSelf = member.id === userId;
    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
    const ok = await confirm(isSelf
      ? {
          title: 'Leave this clinic?',
          message: `You'll lose access to ${clinic.name}. An admin will need to re-add you.`,
          confirmLabel: 'Leave clinic',
          destructive: true,
        }
      : {
          title: 'Remove member?',
          message: `${fullName} will lose access to ${clinic.name}.`,
          confirmLabel: 'Remove',
          destructive: true,
        });
    if (!ok) return;
    try {
      await api.settings.removeMember(clinic.id, member.id);
      onRefresh();
    } catch (err) {
      console.error('Remove failed:', err);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.settings.cancelInvite(clinic.id, inviteId);
      onRefresh();
    } catch (err) {
      console.error('Cancel invite failed:', err);
    }
  };

  return (
    <>
      <div className="border-t border-gray-100 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
        {/* Clinic Name */}
        <div className="flex items-center justify-between pb-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text">Clinic Name</p>
            <p className="text-sm text-gray-500 truncate">{clinic.name}</p>
          </div>
          <button
            onClick={() => setClinicNameModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0 whitespace-nowrap ml-3"
          >
            Update clinic name
          </button>
        </div>

        {/* Subscription Status (web only: the app can't sell or manage plans) */}
        {!isNative && (
        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <p className="text-sm font-semibold text-text">Subscription</p>
            {subscription?.status === 'active' || subscription?.status === 'trialing' ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                No Subscription
              </span>
            )}
          </div>
          {subscription?.status === 'active' || subscription?.status === 'trialing' ? (
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex-shrink-0 whitespace-nowrap ml-3"
            >
              {portalLoading ? 'Loading...' : 'Manage Subscription'}
            </button>
          ) : (
            <button
              onClick={() => navigate(`/choose-plan?clinicId=${clinic.id}`)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors flex-shrink-0 whitespace-nowrap ml-3"
            >
              Subscribe
            </button>
          )}
        </div>
        )}

        {/* Members */}
        <p className="text-sm font-semibold text-text mb-2">Members</p>
        <div className="space-y-0 mb-4">
          {clinic.members.map((member, idx) => (
            <div key={member.id} className={`flex items-center justify-between py-3 px-3 ${idx % 2 === 0 ? 'bg-gray-50' : ''} rounded-lg`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-text truncate">{member.first_name} {member.last_name}</p>
                <p className="text-sm text-gray-500 truncate">{member.email}</p>
              </div>
              {member.id !== userId && (
                <button
                  onClick={() => handleRemoveMember(member)}
                  className="px-4 py-2 text-sm font-medium text-destructive bg-white border border-destructive-30 rounded-lg hover:bg-destructive-10 transition-colors flex-shrink-0 whitespace-nowrap ml-3"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Invites */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-text">Invites</p>
          <button
            onClick={() => setInviteModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1 flex-shrink-0 whitespace-nowrap ml-3"
          >
            <span>+</span> Invite member
          </button>
        </div>
        {clinic.invites.length === 0 ? (
          <div className="border border-[#E6E6E6] rounded-lg p-6 text-center">
            <p className="text-sm font-semibold text-text">No invites</p>
          </div>
        ) : (
          <div className="space-y-0">
            {clinic.invites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between py-3 px-3 bg-gray-50 rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text truncate">
                    {invite.first_name} {invite.last_name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{invite.email}</p>
                </div>
                <button
                  onClick={() => handleCancelInvite(invite.id)}
                  className="px-3 py-1.5 text-sm font-medium text-destructive border border-destructive-30 rounded-lg hover:bg-destructive-10 transition-colors flex-shrink-0 whitespace-nowrap ml-3"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {clinicNameModalOpen && (
        <EditModal
          title="Update clinic name"
          fields={[{ key: 'name', label: 'Clinic Name', defaultValue: clinic.name }]}
          onClose={() => setClinicNameModalOpen(false)}
          onSave={async (values) => {
            await api.settings.updateClinicName(clinic.id, values.name);
            onRefresh();
          }}
        />
      )}

      {inviteModalOpen && (
        <InviteModal
          clinicId={clinic.id}
          userId={userId}
          onClose={() => setInviteModalOpen(false)}
          onInvited={onRefresh}
        />
      )}
      {confirmModal}
    </>
  );
}

export default function Settings() {
  const { profile, signOut } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editPasswordOpen, setEditPasswordOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const { confirm: confirmDelete, confirmModal: deleteConfirmModal } = useConfirm();

  const handleDeleteAccount = async () => {
    setDeleteError('');
    const ok = await confirmDelete({
      title: 'Delete your account?',
      message: "This removes your login and your access to every clinic. Your clinics and their data stay for other members. This can't be undone.",
      confirmLabel: 'Delete account',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.settings.deleteAccount();
      await signOut();
    } catch (err) {
      setDeleteError(err.message && !err.message.startsWith('API error')
        ? err.message
        : "Couldn't delete your account. Please try again.");
    }
  };

  const loadClinics = () => {
    if (!profile?.id) return;
    api.settings.clinics()
      .then((clinicList) => {
        setClinics(clinicList);
        // Load subscription status for each clinic
        clinicList.forEach((clinic) => {
          api.billing.subscription(clinic.id)
            .then((sub) => setSubscriptions((prev) => ({ ...prev, [clinic.id]: sub })))
            .catch(() => setSubscriptions((prev) => ({ ...prev, [clinic.id]: null })));
        });
      })
      .catch(console.error)
      .finally(() => setLoadingClinics(false));
  };

  useEffect(() => {
    loadClinics();
  }, [profile?.id]);

  if (!profile) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Profile card + account rows */}
      <div>
        <LargeTitle title="Settings" />
        <Card className="p-5 flex items-center gap-4">
          <Avatar src={profile.profile_picture_url} name={`${profile.first_name} ${profile.last_name}`} size="lg" />
          <div className="min-w-0">
            <p className="text-[20px] font-bold tracking-[-0.01em] text-[#111] truncate">{profile.first_name} {profile.last_name}</p>
            <p className="text-[14px] text-gray-500 truncate">{profile.email}</p>
          </div>
        </Card>
        <Section title="Account">
          <ListGroup>
            <SettingsRow icon={User} label="Name" value={`${profile.first_name} ${profile.last_name}`} onClick={() => setEditNameOpen(true)} />
            <SettingsRow icon={EnvelopeSimple} label="Email" value={profile.email} onClick={() => setEditEmailOpen(true)} />
            <SettingsRow icon={LockSimple} label="Password" onClick={() => setEditPasswordOpen(true)} />
          </ListGroup>
        </Section>
      </div>


      {editNameOpen && (
        <EditModal
          title="Update name"
          fields={[
            { key: 'firstName', label: 'First Name', defaultValue: profile.first_name },
            { key: 'lastName', label: 'Last Name', defaultValue: profile.last_name },
          ]}
          onClose={() => setEditNameOpen(false)}
          onSave={async (values) => {
            await api.settings.updateProfile(values.firstName, values.lastName);
            window.location.reload();
          }}
        />
      )}


      {editEmailOpen && (
        <EditModal
          title="Update email"
          fields={[{ key: 'email', label: 'Email', type: 'email', defaultValue: profile.email }]}
          onClose={() => setEditEmailOpen(false)}
          onSave={async (values) => {
            await api.settings.updateEmail(values.email);
            window.location.reload();
          }}
        />
      )}

      {editPasswordOpen && (
        <EditModal
          title="Update password"
          fields={[
            { key: 'password', label: 'New Password', type: 'password' },
            { key: 'confirmPassword', label: 'Confirm Password', type: 'password' },
          ]}
          onClose={() => setEditPasswordOpen(false)}
          onSave={async (values) => {
            if (values.password !== values.confirmPassword) throw new Error('Passwords do not match');
            await api.settings.updatePassword(values.password);
          }}
        />
      )}

      {/* Clinic details Section */}
      <h2 className="text-[20px] font-bold tracking-[-0.01em] text-[#111] px-1 mt-7 md:mt-10 mb-2.5">Clinics</h2>
      <div className="bg-white rounded-[22px] shadow-[0_1px_2px_rgba(17,12,34,0.04),0_8px_24px_rgba(17,12,34,0.06)] p-4 md:p-6 mb-6">

        {loadingClinics ? (
          <p className="text-sm text-gray-500">Loading clinics...</p>
        ) : clinics.length === 0 ? (
          <p className="text-sm text-gray-500">You are not a member of any clinic.</p>
        ) : (
          clinics.map((clinic) => (
            <ClinicSection
              key={clinic.id}
              clinic={clinic}
              userId={profile.id}
              subscription={subscriptions[clinic.id]}
              onRefresh={loadClinics}
            />
          ))
        )}
      </div>

      {/* Sign out + delete */}
      <div>
        <ListGroup>
          <SettingsRow icon={SignOut} label="Log out" onClick={() => setLogoutModalOpen(true)} danger />
          {profile.user_type !== 'super_admin' && (
            <SettingsRow icon={Trash} label="Delete account" onClick={handleDeleteAccount} danger />
          )}
        </ListGroup>
        {deleteError && <p className="text-[13px] text-destructive px-1 mt-2">{deleteError}</p>}
      </div>

      {deleteConfirmModal}

      {logoutModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={() => setLogoutModalOpen(false)}>
          <div className="bg-white rounded-t-[28px] md:rounded-xl p-6 pb-[calc(24px+env(safe-area-inset-bottom))] md:pb-6 w-full md:max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text mb-2">Sign out</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to sign out?</p>
            <div className="flex gap-3 [&>button]:flex-1 [&>button]:h-12 md:[&>button]:flex-none md:[&>button]:h-auto">
              <button
                onClick={() => setLogoutModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={signOut}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
